/** Versioned ECDH + HKDF + AES-GCM wrapping, not RFC 9180 HPKE. */
import { copy, fromBase64Url, randomBytes, toBase64Url, utf8, } from './bytes.js';
import { exactKeys, hkdf256, importAesKey, openBox, positiveInteger, sealBox, validId, } from './content.js';
import { domainSeparated, hashCanonical } from './hash.js';
import { exportP256PublicJwk, jwkThumbprint } from './keys.js';
const subtle = () => globalThis.crypto.subtle;
function validateBinding(b) {
    exactKeys(b, ['version', 'accountId', 'epoch', 'recipientId']);
    if (b.version !== 1)
        throw new Error('unsupported wrap version');
    validId(b.accountId);
    validId(b.recipientId);
    positiveInteger(b.epoch);
}
export async function generateAgreementKeyPair(options = {}) {
    return (await subtle().generateKey({ name: 'ECDH', namedCurve: 'P-256' }, options.extractable ?? false, ['deriveBits']));
}
export async function importAgreementPublicKey(jwk) {
    exactKeys(jwk, ['kty', 'crv', 'x', 'y']);
    if (jwk.kty !== 'EC' ||
        jwk.crv !== 'P-256' ||
        typeof jwk.x !== 'string' ||
        typeof jwk.y !== 'string' ||
        fromBase64Url(jwk.x).length !== 32 ||
        fromBase64Url(jwk.y).length !== 32)
        throw new Error('invalid ECDH public key');
    return subtle().importKey('jwk', { ...jwk }, { name: 'ECDH', namedCurve: 'P-256' }, true, []);
}
async function agreementKek(privateKey, publicJwk, aad) {
    const pub = await importAgreementPublicKey(publicJwk);
    const shared = new Uint8Array(await subtle().deriveBits({ name: 'ECDH', public: pub }, privateKey, 256));
    try {
        const raw = await hkdf256(shared, utf8('viko/wrap/ecdh/hkdf/v1'), aad);
        try {
            return await importAesKey(raw);
        }
        finally {
            raw.fill(0);
        }
    }
    finally {
        shared.fill(0);
    }
}
function wrapAad(binding, ephemeral, recipientKeyId) {
    validateBinding(binding);
    return domainSeparated('viko/wrap/ecdh/aad/v1', { ...binding, ephemeral, recipientKeyId });
}
/** Caller MUST verify the recipient against an authenticated key directory first. */
export async function wrapAccountKey(adk, binding, recipient) {
    if (adk.length !== 32)
        throw new Error('ADK must be 256 bits');
    const pair = await generateAgreementKeyPair();
    const ephemeral = await exportP256PublicJwk(pair.publicKey);
    const recipientKeyId = await jwkThumbprint(recipient);
    const aad = wrapAad(binding, ephemeral, recipientKeyId);
    const key = await agreementKek(pair.privateKey, recipient, aad);
    return { version: 1, ephemeral, recipientKeyId, box: await sealBox(key, aad, adk) };
}
/** ECDH alone does not authenticate the sender. Verify the signed ADK commitment before use. */
export async function unwrapAccountKey(wrap, binding, privateKey, ownPublicKey) {
    exactKeys(wrap, ['version', 'ephemeral', 'recipientKeyId', 'box']);
    if (wrap.version !== 1 || wrap.recipientKeyId !== (await jwkThumbprint(ownPublicKey)))
        throw new Error('wrap recipient mismatch');
    const aad = wrapAad(binding, wrap.ephemeral, wrap.recipientKeyId);
    const key = await agreementKek(privateKey, wrap.ephemeral, aad);
    const raw = await openBox(key, aad, wrap.box);
    if (raw.length !== 32) {
        raw.fill(0);
        throw new Error('invalid wrapped ADK');
    }
    return raw;
}
export function createRecoveryCode() {
    return toBase64Url(randomBytes(32));
}
export async function deriveRecoveryKek(code, accountId) {
    validId(accountId);
    if (typeof code !== 'string' || code.length !== 43)
        throw new Error('recovery code must encode 256 random bits');
    const raw = fromBase64Url(code);
    if (raw.length !== 32)
        throw new Error('invalid recovery code');
    try {
        return await secretKek(raw, 'recovery', { accountId });
    }
    finally {
        raw.fill(0);
    }
}
export async function derivePrfKek(output, accountId, credentialId) {
    validId(accountId);
    validId(credentialId);
    if (output.length !== 32)
        throw new Error('WebAuthn PRF output must be 32 bytes');
    return secretKek(output, 'prf', { accountId, credentialId });
}
/** Stable eval.first input; the authenticator applies the WebAuthn PRF domain separation. */
export async function passkeyPrfSalt(accountId, credentialId) {
    validId(accountId);
    validId(credentialId);
    return hashCanonical('viko/prf/salt/v1', { accountId, credentialId });
}
/**
 * v2 salt for single-ceremony sign-in: constant, because a usernameless ceremony doesn't know the
 * credential beforehand (`prf.eval`, not `evalByCredential`). Per-credential separation comes from
 * the authenticator's per-credential PRF secret; the KEK still binds account and credential.
 */
export async function passkeyPrfSaltV2() {
    return hashCanonical('viko/prf/salt/v2', { v: 2 });
}
async function secretKek(secret, purpose, context) {
    const raw = await hkdf256(copy(secret), utf8(`viko/wrap/${purpose}/hkdf/v1`), domainSeparated(`viko/wrap/${purpose}/info/v1`, context));
    try {
        return await importAesKey(raw);
    }
    finally {
        raw.fill(0);
    }
}
export async function wrapWithKek(adk, binding, kek, purpose) {
    validateBinding(binding);
    if (adk.length !== 32 || !['prf', 'recovery'].includes(purpose))
        throw new Error('invalid wrap');
    return sealBox(kek, domainSeparated(`viko/wrap/${purpose}/aad/v1`, binding), adk);
}
export async function unwrapWithKek(box, binding, kek, purpose) {
    validateBinding(binding);
    if (!['prf', 'recovery'].includes(purpose))
        throw new Error('invalid wrap purpose');
    const raw = await openBox(kek, domainSeparated(`viko/wrap/${purpose}/aad/v1`, binding), box);
    if (raw.length !== 32) {
        raw.fill(0);
        throw new Error('invalid wrapped ADK');
    }
    return raw;
}
//# sourceMappingURL=wrapping.js.map